import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { FormValidation, RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import { useSnackbar } from 'notistack';
import fileDownload from 'react-file-download';
import dayjs from '~/timeInit';

import { Box, Button, Stack } from '@mui/material';

import { downloadCharterDepartureManifest } from '~/services/ReportingService';

import Spinner from '../Spinner';

interface FormData {
  startDate?: string;
  endDate?: string;
}

function validateForm(formData: FormData, errors: FormValidation): FormValidation {
  if (!formData.startDate) {
    errors.startDate.addError('Please select a start date');
  }
  if (!formData.endDate) {
    errors.endDate.addError('Please select an end date');
  }
  if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
    errors.endDate.addError('End date must be after start date');
  }
  return errors;
}

const formSchema: RJSFSchema = {
  description: 'Select a date range to download the Charter Departure Manifest.',
  type: 'object',
  required: ['startDate', 'endDate'],
  properties: {
    startDate: {
      title: 'Start Date (inclusive)',
      type: 'string',
      format: 'date',
    },
    endDate: {
      title: 'End Date (exclusive)',
      type: 'string',
      format: 'date',
    },
  },
};

const uiSchema = {
  startDate: {
    'ui:widget': 'date',
  },
  endDate: {
    'ui:widget': 'date',
  },
};

const defaultFormData: FormData = {
  startDate: dayjs().format('YYYY-MM-DD'),
  endDate: dayjs().add(1, 'day').format('YYYY-MM-DD'),
};

export default function ExportCharterDepartureManifestForm() {
  const { enqueueSnackbar } = useSnackbar();
  const [isGenerating, setIsGenerating] = useState(false);

  const handleSubmit = (event: IChangeEvent<FormData>) => {
    const { startDate, endDate } = event.formData;

    setIsGenerating(true);

    downloadCharterDepartureManifest({ startDate, endDate })
      .then((blob) => {
        const isSingleDay = startDate === dayjs(endDate).subtract(1, 'day').format('YYYY-MM-DD');
        const fileName = isSingleDay
          ? `charter_departure_manifest_${startDate}.xlsx`
          : `charter_departure_manifest_${startDate}_to_${dayjs(endDate).subtract(1, 'day').format('YYYY-MM-DD')}.xlsx`;
        fileDownload(blob, fileName);
        enqueueSnackbar('Your report has been downloaded', { variant: 'success' });
      })
      .catch((err) => {
        enqueueSnackbar(err.message, { variant: 'error' });
      })
      .finally(() => {
        setIsGenerating(false);
      });
  };

  return (
    <Box>
      <Form
        className="package-form"
        schema={formSchema}
        uiSchema={uiSchema}
        formData={defaultFormData}
        onSubmit={handleSubmit}
        customValidate={validateForm}
        validator={validator}
        showErrorList={false}
        disabled={isGenerating}
      >
        <Button type="submit" variant="contained" disabled={isGenerating}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box>Download Charter Departure Manifest</Box>
            {isGenerating && <Spinner color="white" size={20} inline />}
          </Stack>
        </Button>
      </Form>
    </Box>
  );
}
